import "./App.css";
import { cn } from "@/lib/utils";
import { Marquee } from "@/components/marquee";
import { ShimmerButton } from "@/components/shimmer-button";
import { BoxReveal } from "@/components/box-reveal";
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
} from "react-router-dom";
import Kontakt from "./kontakt";
import { OmOss } from "./omoss";
import { ServiceCard } from "@/components/service-card";

interface Service {
  title: string;
  image: string;
  description: string;
}

const services: Service[] = [
  {
    title: "Flytt",
    image: "/flytt.jpg",
    description:
      "Vi erbjuder pålitliga och effektiva flyttjänster för både privata och företagsflyttar. Vi hanterar hela processen – från packning och lastning till transport och uppackning – med hög kvalitet och stor hänsyn till dina ägodelar. Med oss kan du känna dig trygg att flytten går snabbt och säkert.",
  },
  {
    title: "Bortforsling",
    image: "/bortforsling.jpg",
    description:
      "Vi erbjuder pålitlig och effektiv bortforsling av skräp, gamla möbler och oönskade föremål för både privatpersoner och företag. Med hög professionalitet och noggrant utförande tar vi hand om allt från hämtning till korrekt avfallshantering, så att du kan fokusera på annat.",
  },
  {
    title: "Trappstädning/Fastighetsstäd",
    image: "/trapp.jpg",
    description:
      "Vi har i många år städat hos flera olika föreningar och städningen kännetecknas av bekymmersfri service och hög kvalitet. Vi erbjuder både engångsstädning och regelbundna avtalsstädningar.",
  },
  {
    title: "Kontorsstädning",
    image: "/kontor.jpg",
    description:
      "Vår företagsstädning kännetecknas av bekymmersfri service med högsta kvalitet. Vår personal trivs väl med våra kunder och motiveras av att leverera bästa möjliga resultat. Vi erbjuder både engångsstädning och regelbundna avtalsstädningar.",
  },
  {
    title: "Hemstädning / Storstädning",
    image: "/hemstäd.jpg",
    description:
      "Crystal Clean Kos gör vardagen enklare för våra kunder. Med lång erfarenhet och noggrant fokus på hög kvalitet tar vårt team hand om alla uppgifter i hemmet, vi tar även stor hänsyn till känsliga områden som golv och ömtåliga möbler.",
  },
  {
    title: "Flyttstädning",
    image: "/flyttstad.jpeg",
    description:
      "Vårt arbetssätt för flyttstädning gör att vi kan utnyttja tiden maximalt. Vi använder alltid rätt utrustning för att säkerställa ett förstklassigt resultat.",
  },
  {
    title: "Visningsstäd",
    image: "/visning.jpg",
    description:
      "Vi har lång erfarenhet av visningsstädning för bostadsföreningar och fastighetsägare. Vår noggranna och pålitliga service säkerställer att allt är i ordning inför visningen, med fokus på hög kvalitet och effektivitet för en bekymmersfri upplevelse för både kunden och potentiella köpare.",
  },
  {
    title: "Byggstädning",
    image: "/bygg.jpg",
    description:
      "Vi erbjuder även byggstädning av alla storlekar oavsett om det gäller dig som privatperson eller företag.",
  },
  {
    title: "Trädgårdsarbete",
    image: "/tradgard.jpg",
    description:
      "Vi erbjuder professionellt trädgårdsarbete som hjälper dig att hålla din trädgård i toppform året runt. Vårt erfarna team tar hand om alla typer av trädgårdsuppgifter, stora som små. Vi erbjuder bland annat klippning av häckar och gräs samt röjning och krattning.",
  },
  {
    title: "Fönsterputsning",
    image: "/window.webp",
    description:
      "Fönsterputsning ger ett välskött intryck i alla miljöer, både i hemmet och på företaget! Vi har lång erfarenhet av fönsterputs och ser alltid till att lämna dina fönster skinande rena!",
  },
];

function App() {
  return (
    <Router>
      <ScrollToHash />
      <Routes>
        {/* Home Page */}
        <Route path="/" element={<Home />} />

        {/* Contact Page */}
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/om-oss" element={<OmOss />} />
      </Routes>
    </Router>
  );
}

export default App;

function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.pathname, location.hash]);
  return null;
}

// Home Component
function Home() {
  return (
    <div className="App bg-white">
      <Header />
      <HeroSection />

      {/* Services */}
      <section id="tjanster" className="scroll-mt-28">
        <div className="flex flex-col items-center w-full bg-white relative z-10">
          <div className="w-full max-w-7xl px-4 sm:px-6">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-4">Våra tjänster</h2>
              <p className="text-gray-600 mt-2">Professionella lösningar för hem och företag.</p>
            </div>
          </div>
          <div className="grid w-full max-w-7xl grid-cols-1 gap-4 p-4 auto-rows-fr md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {services.map((service) => (
              <ServiceCard key={service.title} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Varför välja oss</h2>
            <p className="text-gray-600 mt-2">Pålitliga tjänster, lång erfarenhet och nöjda kunder.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Feature icon="✅" title="Kvalitet i fokus" desc="Noggrannhet och kvalitet i varje uppdrag." />
            <Feature icon="🛡️" title="Försäkrat" desc="Trygghet för dig och dina ägodelar." />
            <Feature icon="🤝" title="Nöjd kund" desc="Vi arbetar tills du är nöjd – varje gång." />
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Redo att få hjälp?</h2>
          <p className="text-gray-700 mt-3">Begär en kostnadsfri offert så återkommer vi snabbt.</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/kontakt">
              <ShimmerButton className="px-6 py-3 bg-softbeige text-black">Begär offert</ShimmerButton>
            </Link>
            <Link to="/#tjanster" className="px-6 py-3 rounded-full bg-softbeige text-black">Våra tjänster</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const reviews = [
  {
    name: "Erik",
    username: "@erik",
    body: "Flyttstädningen var perfekt! Fick tillbaka hela depositionen.",
    img: "https://avatar.vercel.sh/erik",
  },
  {
    name: "Lina",
    username: "@lina",
    body: "Hemstädningen var över förväntan! Kommer boka igen.",
    img: "https://avatar.vercel.sh/lina",
  },
  {
    name: "Johan",
    username: "@johan",
    body: "Fönsterputsningen gjorde verkligen skillnad. Proffsigt utfört!",
    img: "https://avatar.vercel.sh/johan",
  },
  {
    name: "Sofia",
    username: "@sofia",
    body: "Storstädat och skinande rent! Kan varmt rekommendera dem.",
    img: "https://avatar.vercel.sh/sofia",
  },
  {
    name: "Emma",
    username: "@emma",
    body: "Bästa städfirman jag testat! Noggrant och effektivt.",
    img: "https://avatar.vercel.sh/emma",
  },
  {
    name: "Anders",
    username: "@anders",
    body: "Kontoret har aldrig varit fräschare. Grymt jobb!",
    img: "https://avatar.vercel.sh/anders",
  },
];

const firstRow = reviews.slice(0, reviews.length / 2);
const secondRow = reviews.slice(reviews.length / 2);

const ReviewCard = ({
  img,
  name,
  username,
  body,
}: {
  img: string;
  name: string;
  username: string;
  body: string;
}) => {
  return (
    <figure
      className={cn(
        "relative w-64 cursor-pointer overflow-hidden rounded-xl border p-4",
        // light styles
        "border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05]",
        // dark styles
        "dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15]"
      )}
    >
      <div className="flex flex-row items-center gap-2">
        <img className="rounded-full" width="32" height="32" alt="" src={img} />
        <div className="flex flex-col">
          <figcaption className="text-sm font-medium dark:text-white">
            {name}
          </figcaption>
          <p className="text-xs font-medium dark:text-white/40">{username}</p>
        </div>
      </div>
      <blockquote className="mt-2 text-sm">{body}</blockquote>
    </figure>
  );
};

export function Stats() {
  return (
    <div className="stats shadow flex flex-row flex-wrap justify-center gap-5 sm:gap-4 p-2 sm:p-4 divide-none">
      <div className="stat w-24 sm:w-auto flex flex-col items-center text-center border-none">
        <div className="stat-title text-xs sm:text-sm">Erfarenhet</div>
        <div className="stat-value text-black text-4xl">
          20+<span className="text-lg">år</span>
        </div>
      </div>

      <div className="stat w-24 sm:w-auto flex flex-col items-center text-center ">
        <div className="stat-title text-xs sm:text-sm">Nöjda kunder</div>
        <div className="stat-value text-black text-4xl">1200+</div>
      </div>

      <div className="stat w-24 sm:w-auto flex flex-col items-center text-center border-none">
        <div className="stat-title text-xs sm:text-sm">Projekt avslutade</div>
        <div className="stat-value text-black text-4xl">1200+</div>
      </div>
    </div>
  );
}

export function Footer() {
  const footerButtonClass =
    "btn bg-softbeige text-black font-medium border-none normal-case px-6";

  return (
    <footer className="footer footer-center bg-white text-black p-10">
      <aside>
        <img
          src="/logo.png"
          alt="Crystal Clean Kos Logo"
          className="h-24 w-auto"
        />
      </aside>
      <nav className="grid gap-3 justify-items-center text-center">
        <h6 className="footer-title">Kontakta oss nu!</h6>
        <Link to="/kontakt" className={footerButtonClass}>
          Kontakt
        </Link>
        <Link to="/om-oss" className={footerButtonClass}>
          Om oss
        </Link>
        <Link to="/#tjanster" className={footerButtonClass}>
          Våra tjänster
        </Link>
      </nav>
      <p>Copyright © {new Date().getFullYear()} - All rights reserved</p>
    </footer>
  );
}

export function Header() {
  return (
    <div className="navbar sticky top-0 z-50 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-gray-100 px-4">
      {/* Left: Mobile menu */}
      <div className="flex-none lg:hidden">
        <div className="dropdown">
          <button tabIndex={0} className="btn btn-square btn-ghost">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              className="inline-block h-5 w-5 stroke-current"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
          <ul
            tabIndex={0}
            className="menu dropdown-content p-2 shadow bg-base-100 rounded-box w-52 mt-3 z-50"
          >
            <li><Link to="/">Hem</Link></li>
            <li><Link to="/#tjanster">Våra tjänster</Link></li>
            <li><Link to="/om-oss">Om oss</Link></li>
            <li><Link to="/kontakt">Kontakt</Link></li>
          </ul>
        </div>
      </div>

      

      {/* Right: Desktop nav + CTA */}
      <div className="flex-none hidden lg:flex items-center gap-6">
        <Link to="/" className="text-sm text-gray-700 hover:text-gray-900">Hem</Link>
        <Link to="/#tjanster" className="text-sm text-gray-700 hover:text-gray-900">Våra tjänster</Link>
        <Link to="/om-oss" className="text-sm text-gray-700 hover:text-gray-900">Om oss</Link>
        <Link to="/kontakt">
          <ShimmerButton className="px-4 py-2 bg-softbeige text-black">Begär offert</ShimmerButton>
        </Link>
      </div>
    </div>
  );
}

export function MarqueeSection() {
  return (
    <section className="relative w-full border-y bg-stone-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        
        <div className="relative flex w-full flex-col items-center justify-center overflow-hidden md:shadow-sm">
          
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-stone-50"></div>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-stone-50"></div>
        </div>
      </div>
    </section>
  );
}

export function HeroSection() {
  return (
    <section className="relative w-full">
      {/* Background */}
      <div
        className="absolute inset-0 -z-10 bg-center bg-cover hidden sm:block"
        style={{ backgroundImage: "url('/fram.jpg')" }}
      />
      <div className="absolute inset-0 -z-10 bg-black/40 hidden sm:block" />

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-36 md:py-24 text-gray-900">
        <div className="max-w-3xl mx-auto sm:text-center">
          <img
            src="/logo.png"
            alt="Crystal Clean Kos"
            className="block mx-auto mb-4 h-36 md:h-48"
          />
          <BoxReveal boxColor={"#f5f5dc"} duration={0.6}>
            <h1 className="text-3xl sm:text-5xl font-bold leading-tight">
              Städ- och flyttjänster i Stockholm sedan 2004
            </h1>
          </BoxReveal>
          <p className="mt-4 text-lg sm:text-xl text-gray-700">
            Crystal Clean Kos levererar högkvalitativa tjänster med dedikerad personal och konkurrenskraftiga priser.
          </p>
          <div className="mt-8 flex flex-row items-center justify-center gap-3">
            <Link to="/kontakt">
              <ShimmerButton className="px-6 py-3 bg-softbeige text-black">Begär offert</ShimmerButton>
            </Link>
            <Link to="/#tjanster" className="px-6 py-3 rounded-full bg-softbeige text-black">Våra tjänster</Link>
          </div>
          <div className="mt-8">
            <Stats />
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
      <div className="text-2xl">{icon}</div>
      <h4 className="mt-2 text-lg font-semibold text-gray-900">{title}</h4>
      <p className="mt-1 text-gray-600 text-sm">{desc}</p>
    </div>
  );
}

